import { createContext, useContext, useState, ReactNode } from 'react';
import { Snackbar } from '@mui/joy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';

type Color = 'primary' | 'neutral' | 'danger' | 'success' | 'warning';
type Variant = 'plain' | 'outlined' | 'soft' | 'solid';

interface ToastContextProps {
  showToast: (message: string, color?: Color, variant?: Variant) => void;
}

const ToastContext = createContext<ToastContextProps | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [color, setColor] = useState<Color>('success');
  const [variant, setVariant] = useState<Variant>('solid');

  const showToast = (
    message: string, 
    color: Color = 'success', 
    variant: Variant = 'solid'
  ) => {
    setMessage(message);
    setColor(color);
    setVariant(variant);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  // Icon logic based on the color of the toast
  const getIcon = (color: Color) => {
    switch (color) {
      case 'success':
        return <CheckCircleIcon />;
      case 'danger':
        return <ErrorIcon />;
      case 'warning':
        return <WarningIcon />;
      case 'primary':
      case 'neutral':
      default:
        return <InfoIcon />;
    }
  };

  // Determine text color based on variant and color
  const getTextColor = (variant: Variant, color: Color) => {
    if (variant === 'solid' || variant === 'soft') {
      return 'white';
    }
    return 'black';
  };

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        color={color}
        variant={variant}
      >
        <Box display="flex" alignItems="center" gap={1} sx={{ color: getTextColor(variant, color) }}>
          {getIcon(color)}
          <Typography sx={{ color: getTextColor(variant, color) }}>{message}</Typography>
        </Box>
      </Snackbar>
    </ToastContext.Provider>
  );
};
